import { useState, useCallback, useMemo, useEffect, useRef, type ReactElement } from 'react';
import { Chess } from 'chess.js';
import type { Arrow, Square } from 'react-chessboard/dist/chessboard/types';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import { Chessboard } from 'react-chessboard';
import { useGameStore } from '../store/gameStore';
import { engineManager } from '../utils/engineManager';
import type { MaiaLevel } from '../utils/maiaEngine';
import { MoveList } from './MoveList';

interface MoveAnalysis {
  move: string;
  san: string;
  probability: number;
  evaluation: number;
  stockfishLoss: number;
  tone: 'best' | 'good' | 'caution' | 'danger' | 'neutral';
}

interface BlunderMapPoint {
  move: string;
  stockfishLoss: number;
  maiaLikelihood: number;
  color: string;
}

const MAIA_LEVELS: MaiaLevel[] = [1100, 1300, 1500, 1700, 1900];

const moveToneStyles: Record<MoveAnalysis['tone'], string> = {
  danger: 'bg-red-500/20 border border-red-500/40 text-red-300',
  caution: 'bg-yellow-500/10 border border-yellow-500/40 text-yellow-200',
  best: 'bg-emerald-500/10 border border-emerald-500/40 text-emerald-200',
  good: 'bg-sky-500/10 border border-sky-500/40 text-sky-200',
  neutral: 'bg-gray-500/10 border border-gray-500/30 text-gray-200',
};

const evalToWinPercentage = (evaluation: number): number => {
  const winPct = 50 + 50 * (2 / (1 + Math.exp(-0.4 * evaluation)) - 1);
  return Math.round(Math.max(0, Math.min(100, winPct)));
};

const getToneFromLoss = (loss: number): MoveAnalysis['tone'] => {
  if (loss < 0.25) return 'best';
  if (loss < 0.75) return 'good';
  if (loss < 1.75) return 'caution';
  return 'danger';
};

const getColorForMove = (loss: number): string => {
  if (loss < 0.5) return '#22c55e';
  if (loss < 1.5) return '#eab308';
  if (loss < 3) return '#f97316';
  return '#ef4444';
};

const generateMaiaProbabilities = (
  analyses: MoveAnalysis[],
  rating: MaiaLevel,
  maiaBestUci?: string
): number[] => {
  if (analyses.length === 0) return [];
  const ratingFactor = (rating - 1100) / 800;
  const bestKey = maiaBestUci?.slice(0, 4) ?? null;

  const weights = analyses.map((analysis) => {
    const loss = analysis.stockfishLoss;
    let weight: number;
    if (loss < 0.4) weight = 28 + ratingFactor * 35;
    else if (loss < 1.2) weight = 22 + ratingFactor * 12;
    else if (loss < 2.5) weight = 18 - ratingFactor * 8;
    else weight = 12 - ratingFactor * 10;
    if (bestKey && analysis.move.startsWith(bestKey)) weight *= 1.4;
    if (loss > 2 && rating <= 1300) weight *= 1.2;
    return Math.max(weight, 1);
  });

  const total = weights.reduce((sum, w) => sum + w, 0);
  return analyses.map((_, idx) => (weights[idx] / total) * 100);
};

const uciToSan = (fen: string, move: string): string => {
  if (!move) return '';
  const chess = new Chess(fen);
  const from = move.slice(0, 2);
  const to = move.slice(2, 4);
  const promotion = move.length > 4 ? move[4] : undefined;
  const result = chess.move({ from, to, promotion });
  return result?.san ?? move;
};

const extractArrowSquares = (move: string): [Square, Square] | null => {
  if (!move || move.length < 4) return null;
  return [move.slice(0, 2) as Square, move.slice(2, 4) as Square];
};

const renderScatterDot = (props: unknown): ReactElement => {
  const typedProps = props as {
    cx?: number;
    cy?: number;
    payload?: BlunderMapPoint;
  };
  const { cx = 0, cy = 0, payload } = typedProps;
  if (!payload) return <circle cx={0} cy={0} r={0} opacity={0} />;
  const radius = Math.max(6, payload.maiaLikelihood * 0.35);
  const color = payload.color ?? '#94a3b8';
  return (
    <g>
      <circle cx={cx} cy={cy} r={radius} fill={color} opacity={0.75} />
      <text x={cx} y={cy - radius - 6} textAnchor="middle" fill="#e5e7eb" fontSize={10}>
        {payload.move}
      </text>
    </g>
  );
};

interface CollapsibleSectionProps {
  title: string;
  icon?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  id: string;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
}

function CollapsibleSection({ title, icon, children, defaultOpen = true, id, isOpen: controlledOpen, onToggle }: CollapsibleSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const handleToggle = () => {
    const next = !isOpen;
    if (onToggle) onToggle(next);
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
  };

  return (
    <div className="border-b border-gray-800 last:border-b-0">
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-800/30 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#2b2926]"
        aria-expanded={isOpen}
        aria-controls={`section-${id}`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-200 uppercase tracking-wide">
          {icon && <span>{icon}</span>}
          {title}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div id={`section-${id}`} className="p-4 pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

export function AnalysisPanel() {
  const { chess, engineSettings, moveHistory, currentMoveIndex } = useGameStore();
  const currentMove = moveHistory[currentMoveIndex >= 0 ? currentMoveIndex : moveHistory.length - 1];
  const depth = engineSettings.depth ?? 18;
  const currentFen = chess.fen();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [maiaLevel, setMaiaLevel] = useState<MaiaLevel>(1500);
  const [humanMoves, setHumanMoves] = useState<MoveAnalysis[]>([]);
  const [engineMoves, setEngineMoves] = useState<MoveAnalysis[]>([]);
  const [boardArrows, setBoardArrows] = useState<Arrow[]>([]);
  const [winPercentage, setWinPercentage] = useState(50);
  const [positionWarning, setPositionWarning] = useState('');
  const [maiaBest, setMaiaBest] = useState<{ move: string; eval: number; san: string } | null>(null);
  const [showBestMove, setShowBestMove] = useState(true);
  const [autoAnalyze, setAutoAnalyze] = useState(false);
  const lastAnalyzedFen = useRef<string>('');
  const analysisInProgress = useRef(false);

  const analyzeHumanMoves = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && lastAnalyzedFen.current === currentFen) {
        return;
      }
      if (analysisInProgress.current) {
        return;
      }

      analysisInProgress.current = true;
      setIsAnalyzing(true);

      try {
        const fenSnapshot = currentFen;
        const tempChess = new Chess(fenSnapshot);
        const legalMoves = tempChess.moves({ verbose: true });

        if (legalMoves.length === 0) {
          setHumanMoves([]);
          setEngineMoves([]);
          setBoardArrows([]);
          setPositionWarning('');
          lastAnalyzedFen.current = fenSnapshot;
          return;
        }

        await engineManager.setEngine('stockfish');
        const sfRootResult = await engineManager.getBestMove(fenSnapshot, depth);
        setWinPercentage(evalToWinPercentage(sfRootResult.eval));

        const perMoveDepth = Math.max(10, Math.min(depth - 2, 16));
        const candidateMoves = legalMoves.slice(0, Math.min(8, legalMoves.length));
        const analyses: MoveAnalysis[] = [];

        for (const move of candidateMoves) {
          const analysisBoard = new Chess(fenSnapshot);
          const moved = analysisBoard.move(move);
          if (!moved) continue;

          const resultingFen = analysisBoard.fen();
          const moveResult = await engineManager.getBestMove(resultingFen, perMoveDepth);
          const moveEval = -moveResult.eval;
          const loss = Math.max(0, sfRootResult.eval - moveEval);

          analyses.push({
            move: `${move.from}${move.to}${move.promotion ?? ''}`,
            san: move.san,
            probability: 0,
            evaluation: moveEval,
            stockfishLoss: loss,
            tone: getToneFromLoss(loss),
          });
        }

        await engineManager.setEngine('maia', maiaLevel);
        const maiaResult = await engineManager.getBestMove(fenSnapshot, 12);
        const maiaSan = uciToSan(fenSnapshot, maiaResult.bestMove);
        setMaiaBest({ move: maiaResult.bestMove, eval: maiaResult.eval, san: maiaSan });

        const maiaProbabilities = generateMaiaProbabilities(analyses, maiaLevel, maiaResult.bestMove);
        analyses.forEach((analysis, idx) => {
          analysis.probability = Number(maiaProbabilities[idx].toFixed(2));
        });

        const humanMovesData = [...analyses].sort((a, b) => b.probability - a.probability).slice(0, 5);
        const engineMovesData = [...analyses].sort((a, b) => b.evaluation - a.evaluation).slice(0, 5);

        const arrows: Arrow[] = [];
        const seen = new Set<string>();
        const addArrow = (uci: string, color: string) => {
          const squares = extractArrowSquares(uci);
          if (!squares) return;
          const key = `${squares[0]}-${squares[1]}-${color}`;
          if (seen.has(key)) return;
          seen.add(key);
          arrows.push([squares[0], squares[1], color]);
        };

        if (humanMovesData[0] && humanMovesData[0].stockfishLoss > 1.5) {
          addArrow(humanMovesData[0].move, '#ef4444');
        }
        if (engineMovesData[0]) {
          addArrow(engineMovesData[0].move, '#2563eb');
        }
        if (maiaResult.bestMove) {
          addArrow(maiaResult.bestMove, '#a855f7');
        }

        setBoardArrows(arrows);
        setHumanMoves(humanMovesData);
        setEngineMoves(engineMovesData);

        const topHuman = humanMovesData[0];
        const topEngine = engineMovesData[0];
        if (topHuman && topEngine) {
          const loss = topHuman.stockfishLoss;
          const probability = topHuman.probability.toFixed(1);
          if (loss > 3) {
            setPositionWarning(
              `Critical: ${probability}% of Maia ${maiaLevel} players choose ${topHuman.san}, but this collapses the position. ${topEngine.san} is winning.`
            );
          } else if (loss > 1.5) {
            setPositionWarning(
              `Sharp position: ${topHuman.san} tempts humans (${probability}%), yet Stockfish prefers ${topEngine.san}.`
            );
          } else if (loss > 0.5) {
            setPositionWarning(`${topHuman.san} is popular, though ${topEngine.san} squeezes more.`);
          } else {
            setPositionWarning(`Human intuition aligns with engine: ${topHuman.san} is both natural and strongest.`);
          }
        } else {
          setPositionWarning('');
        }

        lastAnalyzedFen.current = fenSnapshot;
      } catch (error) {
        console.error('Human-aware analysis failed:', error);
      } finally {
        analysisInProgress.current = false;
        setIsAnalyzing(false);
      }
    },
    [currentFen, depth, maiaLevel]
  );

  // Auto-analyze effect
  useEffect(() => {
    if (autoAnalyze && currentFen !== lastAnalyzedFen.current && !analysisInProgress.current) {
      const timer = setTimeout(() => {
        analyzeHumanMoves();
      }, 500); // Debounce by 500ms to avoid excessive analysis

      return () => clearTimeout(timer);
    }
  }, [autoAnalyze, currentFen, analyzeHumanMoves]);

  const movesByRatingData = useMemo(() => {
    if (humanMoves.length === 0) return [];
    return MAIA_LEVELS.map((rating) => {
      const ratingFactor = (rating - 1100) / 800;
      const dataPoint: Record<string, number> = { rating };
      humanMoves.slice(0, 5).forEach((move) => {
        const base = move.probability;
        const loss = move.stockfishLoss;
        let adjusted = base;
        if (loss < 0.5) adjusted = base * (0.6 + ratingFactor);
        else if (loss > 2) adjusted = base * (1.5 - ratingFactor);
        else adjusted = base * (0.9 + (1 - Math.abs(0.5 - ratingFactor)) * 0.2);
        dataPoint[move.san] = Number(adjusted.toFixed(1));
      });
      return dataPoint;
    });
  }, [humanMoves]);

  const blunderMapData = useMemo(
    () =>
      humanMoves.map((move) => ({
        move: move.san,
        stockfishLoss: move.stockfishLoss,
        maiaLikelihood: move.probability,
        color: getColorForMove(move.stockfishLoss),
      })),
    [humanMoves]
  );

  const lineColors = useMemo(() => {
    const colors: Record<string, string> = {};
    humanMoves.forEach((move) => {
      colors[move.san] = getColorForMove(move.stockfishLoss);
    });
    return colors;
  }, [humanMoves]);

  return (
    <div className="h-full flex flex-col bg-[#2b2926] text-gray-100 overflow-hidden">
      {/* Move List - Always visible at top */}
      <div className="flex-shrink-0 max-h-[30vh] overflow-hidden border-b border-gray-800">
        <MoveList />
      </div>

      {/* Scrollable Analysis Sections */}
      <div className="flex-1 overflow-y-auto">
        {/* Engine Evaluation */}
        <CollapsibleSection title="Engine Evaluation" icon="⚙️" id="engine" defaultOpen={true}>
          {currentMove ? (
            <div className="space-y-3">
              <div className="bg-[#312e2b] rounded-xl p-4 border border-gray-800/60">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Evaluation</span>
                  {currentMove.depth && (
                    <span className="text-xs text-gray-500 font-mono bg-gray-800 px-2 py-1 rounded">
                      Depth {currentMove.depth}
                    </span>
                  )}
                </div>
                <div className="text-3xl font-bold">
                  {currentMove.mate ? (
                    <span className="text-blue-400">M{Math.abs(currentMove.mate)}</span>
                  ) : (
                    <span className={currentMove.eval > 0 ? 'text-white' : 'text-gray-400'}>
                      {currentMove.eval > 0 ? '+' : ''}
                      {currentMove.eval.toFixed(2)}
                    </span>
                  )}
                </div>
                {currentMove.bestMove && (
                  <div className="text-xs text-gray-400 mt-2">
                    Best: <span className="text-gray-300 font-mono">{currentMove.bestMove}</span>
                  </div>
                )}
              </div>

              <div className="bg-[#312e2b] rounded-xl p-4 border border-gray-800/60">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Move Symbols</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { icon: '‼', label: 'Brilliant', color: '#1abc9c' },
                    { icon: '!', label: 'Great', color: '#3498db' },
                    { icon: '✓', label: 'Best', color: '#95a5a6' },
                    { icon: '⚡', label: 'Excellent', color: '#16a085' },
                    { icon: '📖', label: 'Book', color: '#f39c12' },
                    { icon: '○', label: 'Good', color: '#2ecc71' },
                    { icon: '?!', label: 'Inaccurate', color: '#f1c40f' },
                    { icon: '?', label: 'Mistake', color: '#e67e22' },
                    { icon: '⊘', label: 'Miss', color: '#9b59b6' },
                    { icon: '??', label: 'Blunder', color: '#e74c3c' },
                  ].map(({ icon, label, color }) => (
                    <div key={label} className="flex items-center gap-2">
                      <span style={{ color }} className="font-bold">
                        {icon}
                      </span>
                      <span className="text-gray-400">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-gray-400 text-sm text-center py-4">No move analyzed yet</div>
          )}
        </CollapsibleSection>

        {/* Best Move Suggestion */}
        {currentMove?.bestMove && (
          <CollapsibleSection
            title="Best Move"
            icon="🎯"
            id="best-move"
            defaultOpen={showBestMove}
          >
            <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-700/40 rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Recommended Move</div>
                  <div className="text-2xl font-bold font-mono text-blue-200">{currentMove.bestMove}</div>
                </div>
                <button
                  onClick={() => setShowBestMove(!showBestMove)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title={showBestMove ? 'Hide best move' : 'Show best move'}
                  aria-label={showBestMove ? 'Hide best move' : 'Show best move'}
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showBestMove ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    )}
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-300">
                Stockfish recommends this move with evaluation{' '}
                {currentMove.mate ? `Mate in ${Math.abs(currentMove.mate)}` : `${currentMove.eval > 0 ? '+' : ''}${currentMove.eval.toFixed(2)}`}
              </p>
            </div>
          </CollapsibleSection>
        )}

        {/* Human-Aware Analysis */}
        <CollapsibleSection title="Human AI Analysis" icon="🧠" id="human-ai" defaultOpen={false}>
          <div className="space-y-4">
            {autoAnalyze && (
              <div className="flex items-center gap-2 bg-purple-900/30 border border-purple-600/40 rounded-lg px-3 py-2 mb-3">
                <svg className="w-4 h-4 text-purple-300 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-purple-200 font-medium">Auto-analysis enabled</span>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <select
                value={maiaLevel}
                onChange={(e) => setMaiaLevel(Number(e.target.value) as MaiaLevel)}
                className="bg-[#312e2b] px-3 py-2 rounded-lg border border-gray-700/50 text-sm text-white cursor-pointer hover:border-gray-600 flex-1 min-w-[150px]"
              >
                {MAIA_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    Maia {level}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => analyzeHumanMoves({ force: true })}
                  disabled={isAnalyzing}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                  title={autoAnalyze ? 'Force immediate analysis' : 'Analyze current position'}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                </button>
                <button
                  type="button"
                  onClick={() => setAutoAnalyze((prev) => !prev)}
                  role="switch"
                  aria-checked={autoAnalyze}
                  title={autoAnalyze ? 'Disable auto-analysis' : 'Enable auto-analysis'}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full border-2 transition-all ${autoAnalyze ? 'bg-purple-600 border-purple-500' : 'bg-gray-700 border-gray-600'}`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow-lg transition-transform ${autoAnalyze ? 'translate-x-5' : 'translate-x-0.5'}`}
                  />
                  <span className="sr-only">Toggle auto analysis</span>
                </button>
                <span className={`text-xs font-medium ${autoAnalyze ? 'text-purple-300' : 'text-gray-400'}`}>
                  Auto
                </span>
              </div>
            </div>

            {isAnalyzing && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-400 border-t-transparent" />
              </div>
            )}

            {!isAnalyzing && humanMoves.length > 0 && (
              <>
                <div className="bg-[#312e2b] rounded-xl p-3 border border-gray-800/60 mb-3">
                  <div className="text-xs text-gray-400 mb-2">Position Preview</div>
                  <Chessboard
                    position={currentFen}
                    arePiecesDraggable={false}
                    boardOrientation="white"
                    customBoardStyle={{ borderRadius: '8px' }}
                    customDarkSquareStyle={{ backgroundColor: '#7a5230' }}
                    customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                    customArrows={boardArrows}
                    id="mini-board"
                    boardWidth={280}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-[#312e2b] rounded-xl p-3 border border-gray-800/60">
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Win Probability</div>
                    <div className="text-2xl font-bold text-white">{winPercentage}%</div>
                  </div>

                  {maiaBest && (
                    <div className="bg-[#312e2b] rounded-xl p-3 border border-gray-800/60">
                      <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Maia Suggests</div>
                      <div className="text-xl font-bold text-purple-200">{maiaBest.san}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {maiaBest.eval > 0 ? '+' : ''}
                        {maiaBest.eval.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Human Moves</h4>
                  <div className="space-y-2">
                    {humanMoves.map((move) => (
                      <div
                        key={move.move}
                        className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${moveToneStyles[move.tone]}`}
                      >
                        <span className="font-mono text-white">{move.san}</span>
                        <span>{move.probability.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Engine Moves</h4>
                  <div className="space-y-2">
                    {engineMoves.map((move) => (
                      <div
                        key={move.move}
                        className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${moveToneStyles[move.tone]}`}
                      >
                        <span className="font-mono text-white">{move.san}</span>
                        <span>
                          {move.evaluation > 0 ? '+' : ''}
                          {move.evaluation.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {positionWarning && (
                  <div className="bg-amber-900/20 border border-amber-600/40 rounded-xl p-3">
                    <h4 className="text-xs font-semibold text-amber-200 mb-1">Insight</h4>
                    <p className="text-xs text-gray-200 leading-relaxed">{positionWarning}</p>
                  </div>
                )}

                {movesByRatingData.length > 0 && (
                  <div className="bg-[#312e2b] rounded-xl p-3 border border-gray-800/60">
                    <h4 className="text-xs font-semibold text-gray-300 mb-2">Moves by Rating</h4>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={movesByRatingData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3b36" />
                        <XAxis dataKey="rating" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#9ca3af" unit="%" tick={{ fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            borderRadius: '8px',
                            border: '1px solid #374151',
                            fontSize: '11px',
                          }}
                          formatter={(value: number) => `${value.toFixed(1)}%`}
                        />
                        {Object.keys(lineColors).map((key) => (
                          <Line key={key} type="monotone" dataKey={key} stroke={lineColors[key]} strokeWidth={2} dot={{ r: 2 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {blunderMapData.length > 0 && (
                  <div className="bg-[#312e2b] rounded-xl p-3 border border-gray-800/60">
                    <h4 className="text-xs font-semibold text-gray-300 mb-2">Blunder Map</h4>
                    <ResponsiveContainer width="100%" height={180}>
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" stroke="#3f3b36" />
                        <XAxis type="number" dataKey="stockfishLoss" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                        <YAxis type="number" dataKey="maiaLikelihood" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                        <ZAxis type="number" dataKey="maiaLikelihood" range={[60, 300]} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            borderRadius: '8px',
                            border: '1px solid #374151',
                            fontSize: '11px',
                          }}
                        />
                        <Scatter data={blunderMapData} shape={renderScatterDot} />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
